import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmModal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { Table } from '@/components/ui/Table'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { toastService } from '@/lib/toastService'
import { Box, Heading, HStack, IconButton, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { getDevelopers } from '../api/developersApi'
import { deleteComplex, getComplexes } from '../api/liveComplexesApi'
import { ComplexModal } from '../components/ComplexModal'

const columnHelper = createColumnHelper()

export function ResidentialComplexes() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingComplex, setEditingComplex] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // Separate state for filter objects (for UI display)
  const [cityFilter, setCityFilter] = useState(null)
  const [regionFilter, setRegionFilter] = useState(null)
  const [developerFilter, setDeveloperFilter] = useState(null)

  // Use URL filters hook for search, pagination and filter IDs
  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    city_id: '',
    region_id: '',
    developer_id: '',
    page: 1,
    page_size: 20,
    ordering: 'name',
  })

  // Fetch complexes with filters and pagination
  const { data, isLoading } = useQuery({
    queryKey: ['complexes', filters.search, filters.city_id, filters.region_id, filters.developer_id, filters.page, filters.page_size, filters.ordering],
    queryFn: () => {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
      }
      if (filters.search) params.search = filters.search
      if (filters.city_id) params.city = filters.city_id
      if (filters.region_id) params.region = filters.region_id
      if (filters.developer_id) params.developer = filters.developer_id
      if (filters.ordering) params.ordering = filters.ordering
      return getComplexes(params)
    },
  })

  const totalCount = data?.meta?.pagination?.total_count || 0
  const pageCount = Math.ceil(totalCount / filters.page_size)

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteComplex,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complexes'] })
      toastService.success({
        title: t('common.success'),
        description: t('complexes.complexDeleted'),
      })
      setDeleteConfirm(null)
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('complexes.complexDeleteError'),
      })
    },
  })

  const handleEdit = (complex) => {
    setEditingComplex(complex)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setEditingComplex(null)
    setIsModalOpen(true)
  }

  const handleDelete = (complex) => {
    setDeleteConfirm(complex)
  }

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  const columns = [
    columnHelper.accessor('name', {
      header: t('complexes.name'),
      cell: (info) => (
        <Text fontWeight="600" color="#172B4D">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('city_name', {
      header: t('complexes.city'),
      cell: (info) => <Text>{info.getValue() || '—'}</Text>,
    }),
    columnHelper.accessor('region_name', {
      header: t('complexes.region'),
      cell: (info) => <Text>{info.getValue() || '—'}</Text>,
    }),
    columnHelper.accessor('developer_name', {
      header: t('complexes.developer'),
      cell: (info) => <Text>{info.getValue() || '—'}</Text>,
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: (info) => (
        <HStack spacing={2}>
          <IconButton
            aria-label={t('common.edit')}
            size="sm"
            variant="ghost"
            onClick={() => handleEdit(info.row.original)}
          >
            <FiEdit2 />
          </IconButton>
          <IconButton
            aria-label={t('common.delete')}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => handleDelete(info.row.original)}
          >
            <FiTrash2 />
          </IconButton>
        </HStack>
      ),
    }),
  ]

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <Box p={8}>
      <HStack justify="space-between" mb={6}>
        <Heading size="2xl">{t('complexes.title')}</Heading>
        <Button size="md" onClick={handleCreate} leftIcon={<FiPlus />}>
          {t('complexes.createComplex')}
        </Button>
      </HStack>

      {/* Filters */}
      <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="#DFE1E6" mb={4}>
        <Stack gap={4}>
          <Input
            placeholder={t('complexes.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
          <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
            <Box flex="1">
              <AutocompleteSelect
                placeholder={t('complexes.filterByCity')}
                value={cityFilter}
                onChange={(value) => {
                  setCityFilter(value)
                  updateFilters({ city_id: value?.id || '' })
                }}
                fetchOptions={geolocateApi.getCitiesAutocomplete}
                getOptionLabel={(city) => city.name}
                emptyText={t('common.noResults')}
              />
            </Box>
            <Box flex="1">
              <AutocompleteSelect
                placeholder={t('complexes.filterByRegion')}
                value={regionFilter}
                onChange={(value) => {
                  setRegionFilter(value)
                  updateFilters({ region_id: value?.id || '' })
                }}
                fetchOptions={geolocateApi.getRegionsAutocomplete}
                getOptionLabel={(region) => region.name}
                emptyText={t('common.noResults')}
              />
            </Box>
            <Box flex="1">
              <AutocompleteSelect
                placeholder={t('complexes.filterByDeveloper')}
                value={developerFilter}
                onChange={(value) => {
                  setDeveloperFilter(value)
                  updateFilters({ developer_id: value?.id || '' })
                }}
                fetchOptions={getDevelopers}
                getOptionLabel={(dev) => dev.name}
                emptyText={t('common.noResults')}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box bg="white" borderRadius="md" border="1px solid" borderColor="#DFE1E6">
        <Table
          data={data?.data || []}
          columns={columns}
          enablePagination
          enableSorting
          manualPagination
          manualSorting
          pageCount={pageCount}
          totalCount={totalCount}
          state={{
            pagination: {
              pageIndex: filters.page - 1,
              pageSize: filters.page_size,
            },
            sorting: filters.ordering ? [{
              id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
              desc: filters.ordering.startsWith('-')
            }] : [],
          }}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </Box>

      {/* Create/Edit Modal */}
      <ComplexModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingComplex(null)
        }}
        complex={editingComplex}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={t('complexes.deleteConfirmTitle')}
        message={t('complexes.deleteConfirmMessage', { name: deleteConfirm?.name })}
        confirmText={t('common.delete')}
        isLoading={deleteMutation.isPending}
      />
    </Box>
  )
}

export default ResidentialComplexes
